/**
 * Convite para instalar o painel como aplicativo.
 *
 * Existe porque os dois sistemas se comportam de formas opostas e nenhum deles
 * resolve sozinho:
 *
 * - Android/Chrome dispara `beforeinstallprompt`, mas só depois de a pessoa ter
 *   usado o site um pouco, e o aviso nativo é discreto a ponto de passar batido.
 * - iPhone NUNCA oferece instalar. A única forma é Compartilhar › Adicionar à
 *   Tela de Início, e isso só existe no Safari. Quem abre o link pelo WhatsApp
 *   cai num navegador interno onde a opção nem aparece.
 *
 * Então: no Android, um botão que instala de verdade. No iPhone, a instrução
 * com o desenho do gesto. Instalado, o aviso some sozinho.
 */
import { useEffect, useState } from 'react';

interface PedidoDeInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const CHAVE = 'gl:instalar-dispensado';

function jaInstalado(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // Safari no iPhone não implementa display-mode: standalone em versões antigas.
  return (window.navigator as unknown as { standalone?: boolean }).standalone === true;
}

function ehIphone(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function ehSafari(): boolean {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram|Line\//.test(ua);
}

export function ConviteDeInstalacao() {
  const [pedido, setPedido] = useState<PedidoDeInstalacao | null>(null);
  const [aberto, setAberto] = useState(false);
  const [instruindoIphone, setInstruindoIphone] = useState(false);

  useEffect(() => {
    if (jaInstalado()) return;
    try {
      if (localStorage.getItem(CHAVE) === 'sim') return;
    } catch {
      // Armazenamento bloqueado: mostra o convite, é o menos pior.
    }

    const aoPoder = (e: Event) => {
      e.preventDefault();
      setPedido(e as PedidoDeInstalacao);
      setAberto(true);
    };
    window.addEventListener('beforeinstallprompt', aoPoder);

    // iPhone não dispara evento nenhum: o convite aparece por conta própria.
    if (ehIphone()) setAberto(true);

    return () => window.removeEventListener('beforeinstallprompt', aoPoder);
  }, []);

  function dispensar() {
    setAberto(false);
    setInstruindoIphone(false);
    try {
      localStorage.setItem(CHAVE, 'sim');
    } catch {
      // Sem armazenamento, o convite volta na próxima vez. Paciência.
    }
  }

  if (!aberto) return null;

  if (instruindoIphone) {
    return (
      <div className="instalar" role="dialog" aria-label="Como instalar no iPhone">
        <div>
          <strong>Instalar no iPhone</strong>
          <ol className="instalar-passos">
            <li>
              Toque em <strong>Compartilhar</strong> <span aria-hidden>⬆️</span> na barra do Safari
            </li>
            <li>
              Role e toque em <strong>Adicionar à Tela de Início</strong>
            </li>
            <li>
              Toque em <strong>Adicionar</strong>
            </li>
          </ol>
          <p className="hint">
            Se você abriu por dentro de outro aplicativo, abra o link no Safari primeiro — só lá a
            opção existe.
          </p>
        </div>
        <button type="button" className="small" onClick={dispensar}>
          Entendi
        </button>
      </div>
    );
  }

  return (
    <div className="instalar" role="region" aria-label="Instalar aplicativo">
      <div>
        <strong>Instale o painel no celular</strong>
        <span className="hint">Abre direto, em tela cheia, e recebe os avisos.</span>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="small primary"
          onClick={async () => {
            if (pedido) {
              await pedido.prompt();
              const escolha = await pedido.userChoice;
              if (escolha.outcome === 'accepted') dispensar();
              else setAberto(false);
              return;
            }
            if (ehIphone() && ehSafari()) {
              setInstruindoIphone(true);
              return;
            }
            setInstruindoIphone(true);
          }}
        >
          Instalar
        </button>
        <button type="button" className="small" onClick={dispensar} aria-label="Dispensar">
          Agora não
        </button>
      </div>
    </div>
  );
}
